<div class="panel">
  <div class="panel__group">
    <div class="panel__search">
      <app-button
        type="button"
        color="return"
        icon="agres-return"
        (btnClick)="backToUtilities()"
        [title]="'global.back' | translate"></app-button>
    </div>
  </div>
</div>

<header class="pass-calculator__header">
  <p class="pass-calculator__description">{{ 'pass-to-pass-calculator.description' | translate }}</p>
</header>

<div class="pass-calculator">
  <div class="pass-calculator__progress" aria-live="polite">
    <strong>{{ 'pass-to-pass-calculator.passLabel' | translate: { number: draft.passNumber } }}</strong>
    <span>{{ 'pass-to-pass-calculator.measurementsInMeters' | translate }}</span>
  </div>

  @if (storageUnavailable) {
    <p class="pass-calculator__error" role="status">{{ 'pass-to-pass-calculator.storageUnavailable' | translate }}</p>
  }

  <div class="pass-calculator__layout">
    <section class="pass-calculator__card" aria-labelledby="initial-values-title">
      <h2 id="initial-values-title">{{ 'pass-to-pass-calculator.initialValues' | translate }}</h2>
      @for (field of initialFields; track field) {
        <div class="pass-calculator__field">
          <label [for]="field">
            {{ 'pass-to-pass-calculator.' + field | translate }}
            <span>({{ unitKey(field) | translate }})</span>
          </label>
          <input
            [id]="field"
            type="text"
            [attr.inputmode]="inputMode(field)"
            [ngModel]="draft[field]"
            (ngModelChange)="updateInput(field, $event)"
            (blur)="normalizeInput(field)"
            [disabled]="isFollowUpPass"
            autocomplete="off"
            placeholder="0" />
        </div>
        @if (field === 'lineCount') {
          <div class="pass-calculator__computed">
            <span>{{ 'pass-to-pass-calculator.calculatedWidth' | translate }}</span>
            <output
              >{{ displayNumber(calculation.implementWidth) }}
              {{ 'pass-to-pass-calculator.meterUnit' | translate }}</output
            >
          </div>
        }
      }
      @if (isFollowUpPass) {
        <p class="pass-calculator__note">
          {{ 'pass-to-pass-calculator.passAppliedValues' | translate: { number: draft.passNumber - 1 } }}
        </p>
      }
    </section>

    <section class="pass-calculator__card" aria-labelledby="measurements-title">
      <h2 id="measurements-title">{{ 'pass-to-pass-calculator.fieldMeasurements' | translate }}</h2>
      <fieldset class="pass-calculator__direction">
        <legend>{{ 'pass-to-pass-calculator.turnDirection' | translate }}</legend>
        @for (direction of directions; track direction) {
          <label>
            <input
              type="radio"
              name="turnDirection"
              [value]="direction"
              [ngModel]="draft.turnDirection"
              (ngModelChange)="updateDraft({ turnDirection: $event })" />
            {{ 'pass-to-pass-calculator.' + direction | translate }}
          </label>
        }
      </fieldset>

      <figure class="pass-calculator__diagram">
        <figcaption>
          {{ 'pass-to-pass-calculator.fieldView' | translate }} ·
          {{ turnLabelKey | translate }}
        </figcaption>
        <svg viewBox="0 0 480 230" role="img" [attr.aria-label]="'pass-to-pass-calculator.fieldView' | translate">
          @for (pass of passPositions; track pass; let i = $index) {
            <line
              [attr.x1]="80 + i * 160"
              y1="50"
              [attr.x2]="80 + i * 160"
              y2="210"
              stroke="#b0b0b0"
              stroke-width="3"
              stroke-dasharray="8 6" />
            <circle [attr.cx]="80 + i * 160" cy="25" r="19" fill="#fff" stroke="#b0b0b0" />
            <text [attr.x]="80 + i * 160" y="31" text-anchor="middle">{{ pass }}</text>
          }
          <g [attr.transform]="diagramTransform">
            <path d="M400 198 V86" stroke="#1a3a6b" stroke-width="7" fill="none" />
            <path d="M387 92 L400 75 L413 92 Z" fill="#1a3a6b" />
            <path d="M240 89 V195" stroke="#c0392b" stroke-width="7" fill="none" />
            <path d="M227 189 L240 206 L253 189 Z" fill="#c0392b" />
            <path d="M80 85 C100 36 205 36 238 81" stroke="#2e7d32" stroke-width="3" fill="none" />
            <path d="M225 77 L239 84 L238 69 Z" fill="#2e7d32" />
            <image href="assets/images/pass-to-pass/tractor-planter.webp" x="52" y="95" width="56" height="82" />
          </g>
        </svg>
        <div class="pass-calculator__diagram-measurements">
          @for (measurement of diagramMeasurements; track measurement.label) {
            <span
              >{{ measurement.label }}: {{ measurement.value }}
              {{ 'pass-to-pass-calculator.meterUnit' | translate }}</span
            >
          }
        </div>
      </figure>

      <div class="pass-calculator__pair">
        @for (field of measurementFields; track field) {
          <div class="pass-calculator__field">
            <label [for]="field"
              >{{ 'pass-to-pass-calculator.' + field | translate }}
              <span>({{ 'pass-to-pass-calculator.meterUnit' | translate }})</span></label
            >
            <input
              [id]="field"
              type="text"
              inputmode="decimal"
              [ngModel]="draft[field]"
              (ngModelChange)="updateInput(field, $event)"
              (blur)="normalizeInput(field)"
              autocomplete="off"
              placeholder="0" />
          </div>
        }
      </div>
    </section>

    <section class="pass-calculator__results" aria-labelledby="results-title" aria-live="polite">
      <h2 id="results-title">{{ 'pass-to-pass-calculator.correctedValues' | translate }}</h2>
      <p>{{ 'pass-to-pass-calculator.fillOnTerminal' | translate }}</p>
      @if (edited && calculation.errors.length) {
        <p class="pass-calculator__error" role="status">
          {{ 'pass-to-pass-calculator.validation.' + calculation.errors[0] | translate }}
        </p>
      }
      <div class="pass-calculator__result-list">
        <div class="pass-calculator__result">
          <span>{{ 'pass-to-pass-calculator.correctedWidth' | translate }}</span>
          <output
            >{{ displayNumber(calculation.correctedWidth) }}
            {{ 'pass-to-pass-calculator.meterUnit' | translate }}</output
          >
        </div>
        <div class="pass-calculator__result">
          <span>{{ 'pass-to-pass-calculator.correctedOffset' | translate }}</span>
          <output
            >{{ displayNumber(calculation.correctedOffset, offsetDigits) }}
            {{ 'pass-to-pass-calculator.meterUnit' | translate }}</output
          >
        </div>
      </div>
      <div class="pass-calculator__actions">
        <app-button
          type="button"
          color="return"
          icon="agres-reload"
          [disabled]="confirming"
          [title]="'pass-to-pass-calculator.restart' | translate"
          (btnClick)="restart()"></app-button>
        @if (draft.history.length) {
          <app-button
            type="button"
            color="return"
            icon="agres-return"
            [disabled]="confirming"
            [title]="'pass-to-pass-calculator.previousPass' | translate"
            (btnClick)="previousPass()"></app-button>
        }
        <app-button
          type="button"
          color="primary"
          icon="agres-arrow-right"
          [disabled]="!canAdvance || confirming"
          [title]="'pass-to-pass-calculator.useOnNextPass' | translate: { number: draft.passNumber + 1 }"
          (btnClick)="nextPass()"></app-button>
      </div>
    </section>
  </div>

  @if (draft.history.length) {
    <section class="pass-calculator__history" aria-labelledby="history-title">
      <h2 id="history-title">{{ 'pass-to-pass-calculator.historyTitle' | translate }}</h2>
      <div class="pass-calculator__table-scroll">
        <table>
          <thead>
            <tr>
              <th scope="col">{{ 'pass-to-pass-calculator.historyPass' | translate }}</th>
              <th scope="col">
                {{ 'pass-to-pass-calculator.historyWidth' | translate }} ({{
                  'pass-to-pass-calculator.meterUnit' | translate
                }})
              </th>
              <th scope="col">
                {{ 'pass-to-pass-calculator.historyOffset' | translate }} ({{
                  'pass-to-pass-calculator.meterUnit' | translate
                }})
              </th>
            </tr>
          </thead>
          <tbody>
            @for (entry of draft.history; track entry.passNumber) {
              <tr>
                <th scope="row">{{ 'pass-to-pass-calculator.passLabel' | translate: { number: entry.passNumber } }}</th>
                <td>{{ displayNumber(entry.correctedWidth) }}</td>
                <td>{{ displayNumber(entry.correctedOffset, offsetDigits) }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </section>
  }
</div>
